import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  RadarChart, PolarGrid,
  PolarAngleAxis as PolarAngleAxisBase, PolarRadiusAxis as PolarRadiusAxisBase,
  Radar,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { StatBox } from '../../../components/shared';
import {
  useGetKvsResultSubjectwiseQuery,
  useGetKvsResult3to5Query,
} from '../../../services/kvsResultApi';
import type { SubjectDistributionItem } from '../../../services/kvsResultApi';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarAngleAxis = PolarAngleAxisBase as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarRadiusAxis = PolarRadiusAxisBase as any;

const SUBJECT_COLORS: Record<string, string> = {
  Hindi: '#2563EB',
  English: '#7C3AED',
  Math: '#0891B2',
  Mathematics: '#0891B2',
  Twau: '#059669',
  EVS: '#059669',
  'Computer Science': '#D97706',
};

const getSubjectColor = (subject: string): string =>
  SUBJECT_COLORS[subject] || '#6366F1';

interface GradeBucket {
  grade: string;
  min: number;
  max: number;
  color: string;
}

const GRADES: GradeBucket[] = [
  { grade: 'A+', min: 90, max: 100, color: '#059669' },
  { grade: 'A', min: 75, max: 89, color: '#10B981' },
  { grade: 'B+', min: 60, max: 74, color: '#3B82F6' },
  { grade: 'B', min: 45, max: 59, color: '#F59E0B' },
  { grade: 'C', min: 33, max: 44, color: '#F97316' },
  { grade: 'F', min: 0, max: 32, color: '#EF4444' },
];

function gradeFor(score: number): GradeBucket {
  return GRADES.find((g) => score >= g.min && score <= g.max) || GRADES[GRADES.length - 1];
}

type ClassFilter = 'overall' | '3' | '4' | '5';

const AcademicEvaluation: React.FC = () => {
  const [academicYear, setAcademicYear] = useState<number>(2026);
  const [term, setTerm] = useState<number>(0);
  const [classFilter, setClassFilter] = useState<ClassFilter>('overall');

  const { data: subjectRes, isLoading: subjectLoading } = useGetKvsResultSubjectwiseQuery({ year: academicYear, term });
  const { data: distRes, isLoading: distLoading } = useGetKvsResult3to5Query({ year: academicYear, term });

  const isLoading = subjectLoading || distLoading;

  const subjects: SubjectDistributionItem[] = useMemo(() => {
    const d = subjectRes?.data;
    if (!d) return [];
    return classFilter === 'overall' ? (d.overall || []) : (d.byClass?.[classFilter] || []);
  }, [subjectRes, classFilter]);

  const overallAvg = useMemo(() => {
    if (!subjects.length) return 0;
    return +(subjects.reduce((s, ss) => s + ss.avgWeightage, 0) / subjects.length).toFixed(1);
  }, [subjects]);

  const bestSubject = useMemo(() =>
    [...subjects].sort((a, b) => b.avgWeightage - a.avgWeightage)[0],
  [subjects]);

  const weakSubject = useMemo(() =>
    [...subjects].sort((a, b) => a.avgWeightage - b.avgWeightage)[0],
  [subjects]);

  const totalStudents = useMemo(() => subjects[0]?.totalStudents ?? 0, [subjects]);

  const barData = useMemo(() =>
    subjects.map(ss => ({
      subject: ss.subjectTitle,
      'Avg Weightage': ss.avgWeightage,
      'Avg Grand Total': ss.avgGrandTotal,
    })),
  [subjects]);

  const radarData = useMemo(() =>
    subjects.map(ss => ({
      subject: ss.subjectTitle,
      value: ss.avgWeightage,
    })),
  [subjects]);

  const gradeDistribution = useMemo(() => {
    const d = distRes?.data;
    if (!d) return [];
    const src = classFilter === 'overall' ? d.overall : d.byClass?.[classFilter];
    if (!src) return [];
    return [
      { grade: 'Excellent', count: src.excellent, color: '#059669' },
      { grade: 'Good', count: src.good, color: '#3B82F6' },
      { grade: 'Average', count: src.average, color: '#F59E0B' },
      { grade: 'Below Avg', count: src.belowAverage, color: '#EF4444' },
    ];
  }, [distRes, classFilter]);

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(new Set([2025, 2026, currentYear - 1, currentYear, currentYear + 1])).sort((a, b) => b - a);
  }, []);

  const classLabel = classFilter === 'overall' ? 'Class III–V' : `Class ${classFilter}`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-3 text-gray-500">Loading academic data…</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Academic Evaluation — Preparatory Stage</h1>
        <p className="text-sm text-gray-500 mt-1">
          Subject-wise academic performance for {classLabel} &mdash; {totalStudents.toLocaleString()} students &mdash; {term === 0 ? 'Overall' : `Term ${term}`}, {academicYear}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="classFilter" className="text-xs font-medium text-gray-600">Class</label>
          <select
            id="classFilter"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value as ClassFilter)}
            className="text-sm text-gray-700 bg-transparent focus:outline-none"
          >
            <option value="overall">Overall (3–5)</option>
            <option value="3">Class 3</option>
            <option value="4">Class 4</option>
            <option value="5">Class 5</option>
          </select>
        </div>

        <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
          <select
            id="term"
            value={term}
            onChange={(e) => setTerm(Number(e.target.value))}
            className="text-sm text-gray-700 bg-transparent focus:outline-none"
          >
            <option value="0">Overall</option>
            <option value="1">Term 1</option>
            <option value="2">Term 2</option>
          </select>
        </div>

        <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Year</label>
          <select
            id="academicYear"
            value={academicYear}
            onChange={(e) => setAcademicYear(Number(e.target.value))}
            className="text-sm text-gray-700 bg-transparent focus:outline-none"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox label="Overall Average" value={`${overallAvg}%`} color="text-blue-600" large />
        <StatBox label="Best Subject" value={bestSubject?.subjectTitle ?? '-'} color="text-emerald-600" large />
        <StatBox label="Weakest Subject" value={weakSubject?.subjectTitle ?? '-'} color="text-amber-600" large />
        <StatBox label="Subjects" value={`${subjects.length}`} color="text-indigo-600" large />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subject-wise bar chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Subject-wise Average Weightage (%)</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="subject" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Bar dataKey="Avg Weightage" radius={[6, 6, 0, 0]} barSize={36} name="Avg Weightage (%)">
                {barData.map((entry) => (
                  <Cell key={entry.subject} fill={getSubjectColor(entry.subject)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Subject Radar */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Subject Competency Radar — {classLabel}
          </h3>
          <ResponsiveContainer width="100%" height={320}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar name="Avg Weightage" dataKey="value" stroke="#2563EB" fill="#2563EB" fillOpacity={0.2} strokeWidth={2} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Distribution */}
      {gradeDistribution.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Performance Distribution — {classLabel}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={gradeDistribution} barSize={48}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="grade" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} name="Students">
                {gradeDistribution.map((entry) => (
                  <Cell key={entry.grade} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Subject Detail Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {subjects.map((ss) => {
          const grade = gradeFor(ss.avgWeightage);
          return (
            <div key={ss.subjectTitle} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900">{ss.subjectTitle}</h4>
                <span
                  className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                  style={{ backgroundColor: grade.color + '20', color: grade.color }}
                >
                  Grade {grade.grade}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <p className="text-xs text-gray-500">Avg Weightage</p>
                  <p className="text-lg font-bold" style={{ color: grade.color }}>{ss.avgWeightage.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Avg Grand Total</p>
                  <p className="text-lg font-bold text-gray-900">{ss.avgGrandTotal.toFixed(1)}</p>
                </div>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>Students</span>
                  <span className="font-semibold text-gray-700">{ss.totalStudents.toLocaleString()}</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${ss.avgWeightage}%`, backgroundColor: getSubjectColor(ss.subjectTitle) }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AcademicEvaluation;
